import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  LuNewspaper, LuMail, LuBell, LuTrendingUp, LuCircleCheck,
  LuUsers, LuCalendar, LuBookOpen, LuArrowRight, LuSend,
  LuRss, LuStar, LuZap, LuGlobe, LuShieldCheck,
} from 'react-icons/lu';

const IMG = {
  hero:    'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=1200&q=80&auto=format',
  issue:   'https://images.unsplash.com/photo-1588681664899-f142ff2dc9b1?w=700&q=80&auto=format',
  airport: 'https://avatars.mds.yandex.net/i?id=cc2c16b3f43ff7b6cd782e4fc76db55f0020766a-5888810-images-thumbs&n=13',
  team:    'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=700&q=80&auto=format',
};

const BENEFITS = [
  {
    icon: <LuZap size={22} />,
    title: 'Latest Industry News',
    desc: 'Be first to know about airport technology upgrades, RFID deployments, and baggage system innovations around the world.',
    color: 'var(--primary)',
    bg: 'var(--primary-pale)',
  },
  {
    icon: <LuBookOpen size={22} />,
    title: 'Deep-Dive Articles',
    desc: 'Monthly technical breakdowns covering AI sorting, IoT sensor networks, predictive maintenance, and more — written for aviation enthusiasts and engineers.',
    color: 'var(--accent)',
    bg: 'var(--accent-pale)',
  },
  {
    icon: <LuTrendingUp size={22} />,
    title: 'Stats & Reports',
    desc: 'Exclusive data summaries from the IATA World Baggage Report, airport performance metrics, and mishandling rate trends across global hubs.',
    color: 'var(--accent-3)',
    bg: 'var(--accent-3-pale)',
  },
  {
    icon: <LuGlobe size={22} />,
    title: 'Airport Spotlights',
    desc: "Behind-the-scenes features on the world's busiest baggage systems — from Heathrow to Dubai to Singapore Changi.",
    color: 'var(--accent-2)',
    bg: 'var(--accent-2-pale)',
  },
  {
    icon: <LuShieldCheck size={22} />,
    title: 'Safety Bulletins',
    desc: 'Critical updates on industry safety standards, new regulations, and emergency protocol improvements that affect airports globally.',
    color: 'var(--accent)',
    bg: 'var(--accent-pale)',
  },
  {
    icon: <LuCalendar size={22} />,
    title: 'Event Announcements',
    desc: 'Upcoming aviation conferences, baggage handling expos, and webinars curated specifically for professionals in ground handling and airport operations.',
    color: 'var(--primary)',
    bg: 'var(--primary-pale)',
  },
];

const PAST_ISSUES = [
  {
    month: 'June 2025',
    cover: IMG.airport,
    headline: 'RFID Mandates at 200+ Airports: What Changes in 2026?',
    topics: ['IATA Resolution 753 Update', 'New Deployments', 'Cost Analysis'],
  },
  {
    month: 'May 2025',
    cover: IMG.issue,
    headline: 'AI Sorting Systems: How Machine Learning Eliminated Misrouting',
    topics: ['AI Algorithms', 'Case Studies', 'ROI Data'],
  },
  {
    month: 'April 2025',
    cover: IMG.team,
    headline: 'Singapore Changi Upgrades — 50km Network Gets Smarter',
    topics: ['Airport Spotlight', 'IoT Sensors', 'Automation Level 5'],
  },
];

const STATS = [
  { n: '2,500+',  l: 'Active Readers', d: 'Aviation professionals worldwide' },
  { n: 'Monthly', l: 'Delivery', d: 'First Tuesday of every month' },
  { n: '100%',    l: 'Free Forever',  d: 'No subscriptions or fees' },
  { n: '4.8/5',   l: 'Reader Rating', d: 'Based on subscriber feedback' },
];

const TESTIMONIALS = [
  {
    name: 'Dr. Sarah Mitchell',
    role: 'Aviation Engineer, Heathrow Airport',
    quote: 'The monthly deep-dives are excellent. I regularly share them with my team as supplemental reading on current systems.',
    stars: 5,
  },
  {
    name: 'James Kowalski',
    role: 'MSc Aviation Management Student',
    quote: 'As a student, this newsletter fills in the gap between textbooks and real-world airport operations. Highly recommended.',
    stars: 5,
  }
];

export default function Newsletter() {
  const [email, setEmail]   = useState('');
  const [name, setName]     = useState('');
  const [pref, setPref]     = useState('all');
  const [toast, setToast]   = useState({ show: false, msg: '' });
  const timer               = useRef(null);

  const showToast = (msg) => {
    clearTimeout(timer.current);
    setToast({ show: true, msg });
    timer.current = setTimeout(() => setToast(t => ({ ...t, show: false })), 3500);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    showToast("You're subscribed! Check your inbox for the welcome email.");
    setEmail(''); setName(''); setPref('all');
  };

  return (
    <div className="page-enter" style={{ paddingTop: 66 }}>

      {/* ══ HERO ══ */}
      <div className="nl-page-hero">
        <div className="nl-page-hero-bg" style={{ backgroundImage: `url(${IMG.hero})` }} />
        <div className="nl-page-hero-overlay" />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="nl-page-hero-inner">
            <div className="section-tag" style={{ color: '#fff', background: 'rgba(255,255,255,.18)', border: '1px solid rgba(255,255,255,.3)' }}>
              <LuNewspaper size={13} /> Free Newsletter
            </div>
            <h1 className="nl-page-hero-title">
              Stay Ahead in Airport Technology
            </h1>
            <p className="nl-page-hero-sub">
              Monthly insights on baggage handling systems, RFID innovations, safety updates,
              and the future of airport automation — delivered free to your inbox.
            </p>
            <div className="nl-page-hero-badges">
              <span><LuCircleCheck size={14} /> Free Forever</span>
              <span><LuUsers size={14} /> 2,500+ Readers</span>
              <span><LuBell size={14} /> Monthly Delivery</span>
            </div>
          </div>
        </div>
      </div>

      {/* ══ STATS STRIP ══ */}
      <section className="nl-stats-strip">
        <div className="container">
          <div className="nl-stats-grid">
            {STATS.map((s, i) => (
              <div key={i} className="nl-stat-item">
                <div className="nl-stat-n">{s.n}</div>
                <div className="nl-stat-l">{s.l}</div>
                <div className="nl-stat-d">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ BENEFITS ══ */}
      <section className="page-section bg-dark-2">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom: 52 }}>
            <div className="section-tag">What You'll Receive</div>
            <h2 className="section-title">What You'll Get</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              Curated insights on airport baggage systems, technology innovations, and industry updates
              delivered straight to your inbox every month.
            </p>
          </div>
          <div className="nl-benefits-grid">
            {BENEFITS.map((b, i) => (
              <div key={i} className={`nl-benefit-card reveal delay-${(i % 4) + 1}`}>
                <div className="nl-benefit-icon" style={{ color: b.color, background: b.bg }}>
                  {b.icon}
                </div>
                <div>
                  <div className="nl-benefit-title">{b.title}</div>
                  <div className="nl-benefit-desc">{b.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* ══ PAST ISSUES ══ */}
      <section className="page-section">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom: 48 }}>
            <div className="section-tag">Archive</div>
            <h2 className="section-title">Recent Issues</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              A look at what our subscribers received in recent months — the quality
              and depth you can expect every single issue.
            </p>
          </div>
          <div className="nl-issues-grid">
            {PAST_ISSUES.map((issue, i) => (
              <div key={i} className={`nl-issue-card reveal delay-${i + 1}`}>
                <div className="nl-issue-img">
                  <img src={issue.cover} alt={issue.headline} loading="lazy" />
                  <div className="nl-issue-month">{issue.month}</div>
                </div>
                <div className="nl-issue-body">
                  <div className="nl-issue-headline">{issue.headline}</div>
                  <div className="nl-issue-topics">
                    {issue.topics.map((t, j) => (
                      <span key={j} className="nl-issue-topic">{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* ══ SUBSCRIBE FORM ══ */}
      <section className="page-section bg-dark-2" id="subscribe">
        <div className="container">
          <div className="nl-subscribe-wrap">
            {/* Left: form */}
            <div className="nl-form-card reveal-left">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg,var(--primary),var(--accent))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <LuSend size={20} />
                </div>
                <div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.18rem', fontWeight: 800, color: 'var(--text-2)' }}>Subscribe Now</div>
                  <div style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>Free — unsubscribe any time</div>
                </div>
              </div>
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }} noValidate>
                <div className="form-group">
                  <label htmlFor="nl-name">Full Name</label>
                  <input id="nl-name" type="text" placeholder="Your name" value={name} onChange={e => setName(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label htmlFor="nl-email">Email Address</label>
                  <input id="nl-email" type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label htmlFor="nl-pref">Content Preference</label>
                  <select id="nl-pref" value={pref} onChange={e => setPref(e.target.value)}>
                    <option value="all">All Topics</option>
                    <option value="tech">Technology & Innovation</option>
                    <option value="safety">Safety & Regulations</option>
                    <option value="ops">Operations & Maintenance</option>
                    <option value="airports">Airport Spotlights</option>
                  </select>
                </div>
                <div style={{ background: 'var(--bg-3)', borderRadius: 10, padding: '12px 14px', fontSize: '.8rem', color: 'var(--text-muted)', display: 'flex', gap: 8 }}>
                  <LuShieldCheck size={15} style={{ color: 'var(--accent-3)', flexShrink: 0, marginTop: 1 }} />
                  We never share your email. One-click unsubscribe at any time.
                </div>
                <button type="submit" className="btn btn-primary" style={{ alignSelf: 'flex-start' }}>
                  <LuMail size={16} /> Subscribe Free
                </button>
              </form>
            </div>

            {/* Right: testimonials */}
            <div className="reveal-right" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <div className="section-tag" style={{ display: 'inline-flex' }}>What Readers Say</div>
                <h3 className="section-title" style={{ marginTop: 12, fontSize: '1.4rem' }}>
                  Trusted by Aviation Professionals
                </h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '.9rem', lineHeight: 1.7 }}>
                  From engineering students to airport operations directors — our readers rely on
                  KI INNOVATION's newsletter as their go-to source for baggage system intelligence.
                </p>
              </div>
              {TESTIMONIALS.map((t, i) => (
                <div key={i} className="nl-testimonial">
                  <div className="nl-testimonial-stars">
                    {Array.from({ length: t.stars }).map((_, j) => (
                      <LuStar key={j} size={13} style={{ fill: 'var(--warning)', color: 'var(--warning)' }} />
                    ))}
                  </div>
                  <div className="nl-testimonial-quote">"{t.quote}"</div>
                  <div className="nl-testimonial-author">
                    <div style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--text-2)' }}>{t.name}</div>
                    <div style={{ fontSize: '.77rem', color: 'var(--text-muted)' }}>{t.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Toast */}
      <div className={`toast${toast.show ? '' : ' hidden'}`} role="status" aria-live="polite">
        <LuCircleCheck size={18} style={{ color: 'var(--accent-3)', flexShrink: 0 }} />
        {toast.msg}
      </div>
    </div>
  );
}
